import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    Input,
    Output,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { KalDate } from '@kalidea/kaligraphi';

interface TimelineInterval {
    start: KalDate;
    end: KalDate;
    days: number; // nombre de jour contenu dans l'interval
    slot: number; // nombre de créneau à afficher
}

export interface TimelineSpot extends TimelineRange {
    id: string;
    label: string;
    row?: number;
    above?: string[];
    tooltip?: string;
    classes?: Record<string, boolean>;
}

export interface Timeline {
    id?: string;
    label: string;
    spotsList: TimelineSpot[];
    rowsList?: TimelineSpot[][];
}

export interface TimelineRange {
    from: KalDate;
    to: KalDate;
}

export interface MonthInformation {
    name: string;
    days: number;
    year: number;
}

export function overlap<T extends TimelineRange>(range1: T, range2: T): boolean {
    return range1.from.isBetween(range2.from, range2.to);
}

// en seconde
function getRangeDuration({to, from}: TimelineRange): number {
    return to.diff(from, 'seconds');
}

/**
 * test if spot challengerSpot is above spot baseSpot
 * based on (by priority): start date, duration and name
 */
export function isAbove(baseSpot: TimelineSpot, challengerSpot: TimelineSpot): boolean {
    const f1 = challengerSpot.from.toDate()!;
    const f2 = baseSpot.from.toDate()!;

    return f1 < f2 || // soit A commence avant B
        (f1.getTime() === f2.getTime() // soit ils commencent en même temps
            && (
                // et que A dure plus longtemps que B
                getRangeDuration(challengerSpot) > getRangeDuration(baseSpot)
                || (
                    // ou si ils ont la même durée, on regarde l'ordre alphabétique
                    getRangeDuration(challengerSpot) === getRangeDuration(baseSpot) && challengerSpot.label < baseSpot.label)
            )
        );
}

const ONE_DAY = 3600 * 24;
type ViewType = 'months' | 'weeks' | 'days';

@Component({
    selector: 'app-timeline',
    templateUrl: './shared-timeline.component.html',
    styleUrls: ['./shared-timeline.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SharedTimelineComponent {
    @Output() readonly spotClicked = new EventEmitter<TimelineSpot>();
    @Output() readonly timelineLabelClicked = new EventEmitter<Timeline>();
    @Output() readonly timelineClicked = new EventEmitter<{ timeline: Timeline, date: KalDate }>();
    // comme dans la doc "!" :)
    @ViewChild('viewDiv') viewDiv!: ElementRef<HTMLDivElement>;

    readonly timeslotSize = 30;

    readonly timeSlotByViewType: Record<ViewType, number> = {
        months: 2,
        weeks: 4,
        days: 24,
    };
    // liste des occupations par ligne
    rowsList: TimelineSpot[][] = [[]];

    selected: TimelineSpot | undefined;
    @Input()
    @HostBinding('class')
    viewType: ViewType = 'months';
    private _to: KalDate | undefined;
    private _from: KalDate | undefined;
    private _timelinesList: Timeline[] = [];

    get numberOfDaysByInterval(): number {
        const match: Record<ViewType, number> = {
            months: 14,
            weeks: 7,
            days: 1,
        };
        return match[this.viewType];
    }

    @Input()
    get to(): KalDate {
        return this._to || new KalDate();
    }

    set to(value: KalDate) {
        this._to = value.endOf(this.viewType);
    }

    @Input()
    get from(): KalDate {
        return (this._from || new KalDate()).startOf(this.viewType);
    }

    set from(value: KalDate) {
        this._from = value.startOf('days');
    }

    @Input()
    get timelinesList(): Timeline[] {
        return this._timelinesList;
    }

    set timelinesList(timelinesList: Timeline[]) {
        // on calcul tout de suite les données nécessaires afin de ne pas surcharger de calcul à chaque rendering
        this._timelinesList = this.updateTimelinesList(timelinesList);
    }

    get timelineSize(): number {
        return (this.timelineDuration() / ONE_DAY);
    }

    get numberOfDays(): number {
        return this.to.diff(this.from, 'days');
    }

    get numberOfTimeslotsByDay(): number {
        return this.timeSlotByViewType[this.viewType];
    }

    get weekday(): number {
        return this.from.getDate().weekday;
    }

    get intervalsList(): TimelineInterval[] {
        const list: TimelineInterval[] = [];
        let from = new KalDate(this.from);

        while (from.isBefore(this.to, 'seconds')) {
            const intervalStart = new KalDate(from);
            const intervalEnd = intervalStart.add(1, this.viewType);

            list.push({
                start: intervalStart,
                end: intervalEnd,
                slot: this.numberOfTimeslotsByDay,
                days: intervalEnd.diff(intervalStart)
            });
            from = intervalEnd;
        }
        return list;
    }


    /**
     * calcul la liste des créneaux horaires à afficher
     * @param date
     */
    getTimeSlots(date: KalDate) {
        const numberOfSecondsInSlot = (ONE_DAY / this.numberOfTimeslotsByDay);
        const list = [];
        const start = date.startOf('days');
        for (let i = 0; i < this.numberOfTimeslotsByDay; i++) {
            list.push(start.add(i * numberOfSecondsInSlot, 'seconds'));
        }
        return list;
    }

    getDaysList(): KalDate[] {
        const daysList: KalDate[] = [];
        for (let i = 0; i < this.numberOfDays; i++) {
            daysList.push(this.from.add(i, 'days'));
        }
        return daysList;
    }

    timelineDuration(): number {
        return getRangeDuration({from: this.from, to: this.to});
    }

    getSpotWidth(spot: TimelineSpot): number {
        return getRangeDuration(spot) / ONE_DAY;
    }

    getSpotPosition(spot: TimelineSpot): number {
        const secondsBeforeSpotStart = getRangeDuration({from: this.from, to: spot.from});
        return secondsBeforeSpotStart / ONE_DAY;
    }

    /**
     * modification des timelines lorsqu'on les défini
     */
    updateTimelinesList(timelinesList: Timeline[]): Timeline[] {
        timelinesList.forEach((t, i) => {
            timelinesList[i] = this.calculRowsForSingleTimeline(t);
        });
        return timelinesList;
    }

    /**
     * déclenché lorsqu'on clique un spot d'une timeline
     */
    select(spot: TimelineSpot, $event: MouseEvent): void {
        this.noop($event);
        this.spotClicked.emit(spot);
        this.selected = spot;
    }

    /**
     * déclenché lorsqu'on clique sur le label d'une timeline
     */
    selectLabel(timeline: Timeline, $event: MouseEvent): void {
        this.noop($event);
        this.timelineLabelClicked.emit(timeline);
    }

    /**
     * déclenché lorsqu'on clique sur une timeline
     */
    selectTimeline(timeline: Timeline, $event: MouseEvent) {
        const {offsetX} = $event; // position horizontale du click en PX
        // on var convertir la position en pixel en secondes
        const numberOfSecondBySlotTime = (ONE_DAY / this.numberOfTimeslotsByDay);
        const numberOfSlots = offsetX / this.timeslotSize;
        const numberOfSeconds = numberOfSlots * numberOfSecondBySlotTime;
        const date = this.from.add(numberOfSeconds, 'seconds');
        this.timelineClicked.emit({timeline, date});
    }

    isEndOfWeek(date: KalDate) {
        return [6, 7].includes(date.getDate().weekday);
    }

    noop($event: MouseEvent) {
        // il faut stopper la propagation pour qu'il n'y ai pas un autre évenement qui soit envoyé
        $event.stopPropagation();
    }

    get numberOfPixelByInterval(): number {
        const numberOfSlotByInterval = this.numberOfTimeslotsByDay * this.numberOfDaysByInterval;
        return numberOfSlotByInterval * this.timeslotSize;
    }

    get actualInterval(): number {
        // quelle est la position actuelle du scroll
        const actualScrollPosition = this.viewDiv.nativeElement.scrollLeft;
        return Math.floor(actualScrollPosition / this.numberOfPixelByInterval);
    }

    nextInterval() {
        this.viewDiv.nativeElement.scroll({
            left: (this.actualInterval + 1) * this.numberOfPixelByInterval,
            behavior: 'smooth'
        })
    }

    previousInterval() {
        if( this.actualInterval > 0 ){
            this.viewDiv.nativeElement.scroll({
                left: (this.actualInterval - 1) * this.numberOfPixelByInterval,
                behavior: 'smooth'
            })
        }

    }

    /**
     * calcul les données à afficher pour une timeline
     */
    private calculRowsForSingleTimeline(timeline: Timeline): Timeline {
        const rowsList: TimelineSpot[][] = [
            [] // ligne de départ
        ];
        // on tri par ordre de priorité
        const spotsList = timeline.spotsList.sort((a, b) => isAbove(a, b) ? 1 : -1);

        // pour chaque spot, on vérifie si il est en concurrence avec un autre spot
        for (const spot of spotsList) {
            // on regarde sur chacune des lignes si y'a une place
            for (let i = 0; i < rowsList.length; i++) {
                const row = rowsList[i];
                if (row.every(challenger => !overlap(spot, challenger))) {
                    spot.row = i;
                    row.push(spot);
                    break;
                }
            }
            // si on a pas eu de place, on ajoute une ligne
            if (spot.row === undefined) {
                spot.row = rowsList.length;
                rowsList.push([spot]);
            }
        }
        timeline.spotsList = spotsList;
        timeline.rowsList = rowsList;

        return timeline;
    }
}
